import { useEffect, useMemo, useRef } from "react";

import { createSessionResultsCache } from "./sessionResultsCache";
import type { SessionResultsCacheModule } from "./types";

type RangeCache = ReturnType<typeof createSessionResultsCache>;

// #389: a session-results cache per range preset, for a panel that reads its
// whole range page by page (useSessionResults' `range`, as the report does)
// instead of the newest 200 rows: 6 or 12 months can hold more. Keyed by the
// preset, not by the exact interval, so the minute ticking does not refetch.
// When the shared cache drops (a saved workout), every one of these drops
// too, and the mounted panel refetches its range.
export function useRangeResultsCache(shared: SessionResultsCacheModule, preset: string): RangeCache {
  const kept = useRef(new Map<string, RangeCache>());
  useEffect(
    () =>
      shared.subscribe(() => {
        for (const cache of kept.current.values()) cache.invalidate();
      }),
    [shared],
  );
  return useMemo(() => {
    const cache = kept.current.get(preset) ?? createSessionResultsCache();
    kept.current.set(preset, cache);
    return cache;
  }, [preset]);
}
